import {
  Job,
  JobQueryParams,
  JobResponse,
  JobListResponse,
  CreateJobData,
  UpdateJobData,
} from '@/Types/JobTyypes';

export class JobService {
  private baseUrl: string;

  constructor() {
    const apiUrl = process.env.BACKEND_API_URL || 'http://localhost:5000';
    this.baseUrl = `${apiUrl}/api/jobs`;
  }

  /*************************************************
   * PUBLIC JOB OPERATIONS
   **************************************************/

  /**
   * Get all jobs with optional filtering and pagination
   */
  async getAllJobs(params?: JobQueryParams): Promise<JobListResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
      }

      const url = queryParams.toString()
        ? `${this.baseUrl}?${queryParams}`
        : this.baseUrl;

      const response = await fetch(url, {
        next: {
          revalidate: 30,
          tags: ['jobs'],
        },
        // Add timeout
        signal: AbortSignal.timeout(10000), // 10 second timeout
      });

      if (!response.ok) {
        // Handle 503 gracefully by returning empty dataset
        if (response.status === 503) {
          console.warn('⚠️ Backend service unavailable (503), returning empty dataset');
          return {
            success: true,
            data: [],
            pagination: { total: 0, page: 1, limit: 10, pages: 0 }
          };
        }
        
        const errorText = await response.text();
        console.error('❌ Error response body:', errorText);
        throw new Error(
          `Failed to fetch jobs: ${response.statusText} (${response.status})`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Handle timeout errors
      if (error instanceof Error && error.name === 'TimeoutError') {
        console.warn('⚠️ Request timeout, returning empty dataset');
        return {
          success: true,
          data: [],
          pagination: { total: 0, page: 1, limit: 10, pages: 0 }
        };
      }
      
      // Handle network errors (e.g., ECONNREFUSED)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.warn('⚠️ Network error, backend may be down. Returning empty dataset');
        return {
          success: true,
          data: [],
          pagination: { total: 0, page: 1, limit: 10, pages: 0 }
        };
      }
      
      console.error('❌ Error fetching jobs:', error);
      throw error;
    }
  }

  /**
   * Get job by ID
   */
  async getJobById(id: string): Promise<JobResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        next: {
          revalidate: 0,
          tags: ['job', `job-${id}`],
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { success: false, error: 'Job not found' };
        }
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error(`Failed to fetch job: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`❌ Error fetching job by ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get job by slug
   */
  async getJobBySlug(slug: string): Promise<JobResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/slug/${slug}`, {
        next: {
          revalidate: 0,
          tags: ['job', `job-${slug}`],
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { success: false, error: 'Job not found' };
        }
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error(`Failed to fetch job: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`❌ Error fetching job by slug ${slug}:`, error);
      throw error;
    }
  }

  /**
   * Get only open jobs (active and not expired)
   */
  async getOpenJobs(params?: JobQueryParams): Promise<JobListResponse> {
    try {
      const queryParams: JobQueryParams = {
        ...params,
        onlyOpen: true,
      };

      return await this.getAllJobs(queryParams);
    } catch (error) {
      console.error('❌ Error fetching open jobs:', error);
      throw error;
    }
  }

  /**
   * Get jobs by location
   */
  async getJobsByLocation(
    location: string,
    params?: JobQueryParams
  ): Promise<JobListResponse> {
    try {
      const queryParams: JobQueryParams = {
        ...params,
        location,
      };

      return await this.getAllJobs(queryParams);
    } catch (error) {
      console.error(`❌ Error fetching jobs for location ${location}:`, error);
      throw error;
    }
  }

  /**
   * Get jobs by department
   */
  async getJobsByDepartment(
    department: string,
    includeInactive: boolean = false
  ): Promise<JobListResponse> {
    try {
      const url = includeInactive
        ? `${this.baseUrl}/department/${department}?includeInactive=true`
        : `${this.baseUrl}/department/${department}`;

      const response = await fetch(url, {
        next: {
          revalidate: 0,
          tags: ['jobs', `department-${department}`],
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch jobs by department: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error(
        `❌ Error fetching jobs for department ${department}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Search jobs
   */
  async searchJobs(
    searchTerm: string,
    params?: JobQueryParams
  ): Promise<JobListResponse> {
    try {
      const queryParams: JobQueryParams = {
        ...params,
        search: searchTerm,
      };

      return await this.getAllJobs(queryParams);
    } catch (error) {
      console.error('❌ Error searching jobs:', error);
      throw error;
    }
  }

  /**
   * Get latest jobs
   */
  async getLatestJobs(limit: number = 6): Promise<JobListResponse> {
    try {
      const response = await fetch(`${this.baseUrl}?limit=${limit}&onlyOpen=true`, {
        next: {
          revalidate: 300,
          tags: ['latest-jobs'],
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch latest jobs: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error fetching latest jobs:', error);
      throw error;
    }
  }

  /**
   * Get expiring jobs (closing soon)
   */
  async getExpiringJobs(days: number = 7): Promise<JobListResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/expiring?days=${days}`, {
        next: {
          revalidate: 3600,
          tags: ['expiring-jobs'],
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch expiring jobs: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error fetching expiring jobs:', error);
      throw error;
    }
  }

  /**
   * Get open jobs count
   */
  async getOpenJobsCount(): Promise<{ success: boolean; count?: number; error?: string }> {
    try {
      const response = await this.getOpenJobs({ limit: 1 });
      
      if (response.success && response.pagination) {
        return {
          success: true,
          count: response.pagination.total,
        };
      }

      return { success: false, error: 'Failed to get job count' };
    } catch (error) {
      console.error('❌ Error fetching job count:', error);
      throw error;
    }
  }

  /*************************************************
   * ADMIN JOB OPERATIONS (Require Authentication)
   **************************************************/

  /**
   * Create a new job
   */
  async createJob(
    jobData: CreateJobData,
    token: string
  ): Promise<JobResponse> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(jobData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create job');
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error creating job:', error);
      throw error;
    }
  }

  /**
   * Update a job
   */
  async updateJob(
    id: string,
    jobData: UpdateJobData,
    token: string
  ): Promise<JobResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(jobData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update job');
      }

      return await response.json();
    } catch (error) {
      console.error(`❌ Error updating job ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a job
   */
  async deleteJob(id: string, token: string): Promise<JobResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete job');
      }

      return await response.json();
    } catch (error) {
      console.error(`❌ Error deleting job ${id}:`, error);
      throw error;
    }
  }

  /**
   * Toggle job active status
   */
  async toggleJobStatus(id: string, token: string): Promise<JobResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}/toggle-status`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to toggle job status');
      }

      return await response.json();
    } catch (error) {
      console.error(`Error toggling job status ${id}:`, error);
      throw error;
    }
  }

  /*************************************************
   * UTILITY METHODS
   **************************************************/

  /**
   * Check if a job is still open
   */
  isJobOpen(job: Job): boolean {
    return job.isActive && new Date(job.closingDate) > new Date();
  }

  /**
   * Get days until closing
   */
  getDaysUntilClosing(job: Job): number {
    const today = new Date();
    const closingDate = new Date(job.closingDate);
    const diffTime = closingDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  /**
   * Format salary range
   */
  formatSalaryRange(job: Job): string {
    if (!job.salaryRange) return 'Competitive';

    const { min, max, currency } = job.salaryRange;

    if (min && max) {
      return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()}`;
    } else if (min) {
      return `From ${currency} ${min.toLocaleString()}`;
    } else if (max) {
      return `Up to ${currency} ${max.toLocaleString()}`;
    }

    return 'Competitive';
  }

  /**
   * Format job type badge
   */
  getJobTypeBadgeColor(jobType?: string): string {
    const colors: Record<string, string> = {
      'Full-time': 'bg-blue-100 text-blue-800',
      'Part-time': 'bg-green-100 text-green-800',
      'Contract': 'bg-purple-100 text-purple-800',
      'Internship': 'bg-yellow-100 text-yellow-800',
      'Temporary': 'bg-gray-100 text-gray-800',
    };

    return jobType ? colors[jobType] || 'bg-gray-100 text-gray-800' : 'bg-gray-100 text-gray-800';
  }
}

// Export singleton instance
export const jobService = new JobService();
export default jobService;